/* Layerline theme scripts. Built as custom elements so they re-initialise
   automatically when sections are added or edited in the theme editor. */

(function () {
  'use strict';

  /* ---------- Slideshow ---------- */
  class LlSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.slide'));
      this.tabs = Array.from(this.querySelectorAll('.slideshow__tab'));
      if (this.slides.length < 2) return;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = parseInt(this.dataset.speed || '6', 10) * 1000;
      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => { this.go(i); this.restart(); }));
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());
      this.addEventListener('shopify:block:select', (e) => { this.pause(); this.go(this.slides.indexOf(e.target)); });
      this.addEventListener('shopify:block:deselect', () => this.play());
      let startX = null;
      this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) { this.go(this.index + (dx < 0 ? 1 : -1)); this.restart(); }
        startX = null;
      });
      this.play();
    }
    disconnectedCallback() { clearInterval(this.timer); }
    go(i) {
      if (i < 0 || Number.isNaN(i)) i = this.slides.length - 1;
      if (i >= this.slides.length) i = 0;
      if (i !== this.index) {
        // Keep the outgoing photo zooming while it fades, so it doesn't snap back to full size mid-transition.
        const old = this.slides[this.index];
        old.classList.add('is-leaving');
        clearTimeout(old._leaveTimer);
        old._leaveTimer = setTimeout(() => old.classList.remove('is-leaving'), 700);
      }
      clearTimeout(this.slides[i]._leaveTimer);
      this.slides[i].classList.remove('is-leaving');
      this.index = i;
      this.slides.forEach((s, n) => { s.classList.toggle('is-active', n === i); s.setAttribute('aria-hidden', n === i ? 'false' : 'true'); s.inert = n !== i; });
      this.tabs.forEach((t, n) => {
        t.classList.remove('is-active'); void t.offsetWidth; // restart progress bar
        t.classList.toggle('is-active', n === i); t.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
      const tab = this.tabs[i];
      if (tab && tab.parentElement) {
        const strip = tab.parentElement;
        strip.scrollTo({ left: tab.offsetLeft - strip.clientWidth / 2 + tab.clientWidth / 2, behavior: 'smooth' });
      }
    }
    play() {
      if (!this.autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.classList.remove('is-paused');
      clearInterval(this.timer);
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
    pause() { this.classList.add('is-paused'); clearInterval(this.timer); }
    restart() { this.pause(); this.play(); }
  }
  customElements.define('ll-slideshow', LlSlideshow);

  /* ---------- Horizontal rails ---------- */
  class LlRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.rail__track');
      this.prev = this.querySelector('.rail__btn--prev');
      this.next = this.querySelector('.rail__btn--next');
      if (!this.track) return;
      const step = () => this.track.clientWidth * 0.85;
      if (this.prev) this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (this.next) this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    disconnectedCallback() { window.removeEventListener('resize', this.update); }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }
  customElements.define('ll-rail', LlRail);

  /* ---------- Header: mobile menu + search ---------- */
  class LlHeader extends HTMLElement {
    connectedCallback() {
      const menu = this.querySelector('.mobile-menu');
      this.querySelectorAll('[data-menu-open]').forEach((b) => b.addEventListener('click', () => {
        menu.classList.add('is-open'); document.body.style.overflow = 'hidden';
        const close = menu.querySelector('[data-menu-close]'); if (close) close.focus();
      }));
      this.querySelectorAll('[data-menu-close]').forEach((b) => b.addEventListener('click', () => {
        menu.classList.remove('is-open'); document.body.style.overflow = '';
      }));
      const panel = this.querySelector('.search-panel');
      this.querySelectorAll('[data-search-toggle]').forEach((b) => b.addEventListener('click', (e) => {
        if (!panel) return;
        e.preventDefault();
        panel.classList.toggle('is-open');
        if (panel.classList.contains('is-open')) panel.querySelector('input').focus();
      }));
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (menu) { menu.classList.remove('is-open'); document.body.style.overflow = ''; }
        if (panel) panel.classList.remove('is-open');
      });
    }
  }
  customElements.define('ll-header', LlHeader);

  /* ---------- Quantity buttons ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const input = btn.parentElement.querySelector('input');
    const min = parseInt(input.min || '0', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qty, 10));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart page: submit on quantity change ---------- */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty]');
    if (!input) return;
    clearTimeout(input._t);
    input._t = setTimeout(() => input.form.submit(), 450);
  });

  /* ---------- Product variant picker ---------- */
  class LlProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.addEventListener('change', (e) => { if (e.target.matches('[data-option]')) this.onChange(); });
      this.markAvailability();
    }
    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-group]')).map((group) => {
        const checked = group.querySelector('input:checked');
        const select = group.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }
    onChange() {
      const opts = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.querySelectorAll('[data-option-group]').forEach((group, i) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = opts[i];
      });
      this.markAvailability();
      this.render(variant);
    }
    markAvailability() {
      const opts = this.selectedOptions();
      this.querySelectorAll('[data-option-group]').forEach((group, i) => {
        group.querySelectorAll('input[data-option]').forEach((input) => {
          const test = opts.slice(); test[i] = input.value;
          const ok = this.product.variants.some((v) => v.available && v.options.every((o, n) => o === test[n]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    }
    render(variant) {
      const btn = this.querySelector('[data-add-button]');
      const btnText = btn && btn.querySelector('span');
      const price = this.querySelector('[data-price]');
      const compare = this.querySelector('[data-compare-price]');
      const badge = this.querySelector('[data-sale-badge]');
      const stock = this.querySelector('[data-stock]');
      const fmt = (cents) => this.formatMoney(cents);
      if (!variant) {
        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = this.dataset.unavailableText;
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      if (price) price.textContent = fmt(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compare) { compare.textContent = onSale ? fmt(variant.compare_at_price) : ''; compare.hidden = !onSale; }
      if (badge) badge.hidden = !onSale;
      if (btn) btn.disabled = !variant.available;
      if (btnText) btnText.textContent = variant.available ? this.dataset.addText : this.dataset.soldOutText;
      if (stock) {
        stock.classList.toggle('is-out', !variant.available);
        stock.textContent = variant.available ? (stock.dataset.inText || this.dataset.inStockText) : this.dataset.soldOutText;
      }
      if (variant.featured_media) {
        const media = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        const gallery = this.querySelector('.product__gallery');
        if (media && gallery) {
          if (window.matchMedia('(max-width: 989px)').matches) {
            gallery.scrollTo({ left: media.offsetLeft - gallery.offsetLeft - 16, behavior: 'smooth' });
          } else if (media !== gallery.firstElementChild) {
            gallery.prepend(media);
          }
        }
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = (cents / 100);
      const withDelims = (n, d, t, dec) => {
        const parts = n.toFixed(d).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
        return parts.join(dec);
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
          case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
          default: return withDelims(value, 2, ',', '.');
        }
      });
    }
  }
  customElements.define('ll-product', LlProduct);

  /* ---------- Collection: auto-submit sort ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-autosubmit]')) e.target.form.submit();
  });

  /* ---------- Reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
  }, { rootMargin: '0px 0px -8% 0px' }) : null;
  const observe = (root) => (root || document).querySelectorAll('.reveal:not(.is-visible)').forEach((el) => {
    if (io && !window.Shopify?.designMode) io.observe(el); else el.classList.add('is-visible');
  });
  document.addEventListener('DOMContentLoaded', () => observe());
  document.addEventListener('shopify:section:load', (e) => observe(e.target));
})();
